import React from "react";
import Modal from "../../../shared/ui/Modal";

import { useAuth } from "../../auth/AuthContext";
import { retrieveItem } from "../items.api";
import CompareItemPriceSnapshot, {
    type CompareItemPriceSnapshotData,
} from "./CompareItemPriceSnapshot";

interface SpecifiedCompareItemsModalProps {
    show: boolean;
    itemId: number | null;
    onHide: () => void;
}

const REFRESH_INTERVAL = 3000;

const SpecifiedCompareItemsModal: React.FC<SpecifiedCompareItemsModalProps> = ({
    show,
    itemId,
    onHide,
}) => {
    const auth = useAuth();
    const [snapshot, setSnapshot] = React.useState<CompareItemPriceSnapshotData | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!show || itemId === null) {
            return;
        }

        let isCancelled = false;
        let refreshTimer: number | undefined;
        setSnapshot(null);
        setErrorMessage("");

        const fetchSpecifiedItems = async () => {
            if (isCancelled) {
                return;
            }

            setIsLoading(true);
            await retrieveItem({
                auth,
                id: String(itemId),
                setResult: (data: unknown) => {
                    if (!isCancelled) {
                        const itemData = data as {
                            compare_items_price_snapshot?: CompareItemPriceSnapshotData;
                        };
                        setSnapshot(itemData.compare_items_price_snapshot || null);
                        setErrorMessage("");
                    }
                },
                setErrorMessage: (message: string) => {
                    if (!isCancelled) {
                        setErrorMessage(message);
                    }
                },
            });
            if (!isCancelled) {
                setIsLoading(false);
                refreshTimer = window.setTimeout(() => {
                    void fetchSpecifiedItems();
                }, REFRESH_INTERVAL);
            }
        };

        void fetchSpecifiedItems();

        return () => {
            isCancelled = true;
            if (refreshTimer !== undefined) {
                window.clearTimeout(refreshTimer);
            }
        };
    }, [auth, itemId, show]);

    return (
        <Modal show={show} onHide={onHide} size="xl" scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5">
                    경쟁상품 가격 스냅샷 {isLoading && (
                        <small className="text-muted">({snapshot ? "갱신 중" : "조회 중"}...)</small>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && (
                    <div className="alert alert-danger" role="alert">
                        {errorMessage}
                    </div>
                )}

                <CompareItemPriceSnapshot snapshot={snapshot} />
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onHide}>
                    닫기
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default SpecifiedCompareItemsModal;
